/**
 * Listing cards and clickable links in the chat widget.
 *
 * The AI's listing search (backend services/salesAgent/listingSearchTool.ts)
 * sends the listings it found with its reply; they show as a row of cards
 * under it. Links in the AI's text become clickable; before, a visitor had to
 * copy them by hand.
 */

export interface ListingCard {
  id: string;
  /** sale | rent | product | service (a business in the site's directory). */
  kind?: string;
  title: string;
  priceText?: string | null;
  year?: string | null;
  length?: string | null;
  location?: string | null;
  imageUrl?: string | null;
  url: string;
}

const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** The site's page with every result, shown as a worded link under the cards. */
export interface ListingMore {
  url: string;
  label: string;
}

/** Only https links, escaped for an attribute; anything else is dropped. */
const safeUrl = (value: unknown): string => (/^https:\/\/[^\s"'<>`]+$/.test(String(value ?? '')) ? escapeHtml(value) : '');

/** A bare link: at the start, after a space or "(", so a link already inside an anchor is left alone. */
const BARE_LINK = /(^|[\s(])((?:https?:\/\/|www\.)[^\s<>"'`]+)/g;

/**
 * Links in text that is ALREADY escaped become anchors: http(s) ones and the
 * "www.teknevemarin.com/…" the AI often writes without a scheme. Quotes and
 * angle brackets end a link: a quote inside href would break out of it.
 */
export function linkifyEscaped(escapedText: string): string {
  return escapedText.replace(BARE_LINK, (_match, lead: string, raw: string) => {
    let url = raw;
    let trail = '';
    // Sentence punctuation after a link is not part of it; a closing bracket
    // is only when it has no opening one in the link ("brand_id=[94]" keeps it).
    for (;;) {
      const last = url.slice(-1);
      const unbalanced =
        (last === ')' && (url.match(/\(/g)?.length ?? 0) < (url.match(/\)/g)?.length ?? 0)) ||
        (last === ']' && (url.match(/\[/g)?.length ?? 0) < (url.match(/\]/g)?.length ?? 0));
      if (!/[.,;:!?]/.test(last) && !unbalanced) break;
      trail = last + trail;
      url = url.slice(0, -1);
    }
    if (!url.includes('.') || url.length < 8) return `${lead}${raw}`;
    const href = url.startsWith('www.') ? `https://${url}` : url;
    return `${lead}<a href="${href}" target="_blank" rel="noopener noreferrer">${linkLabel(url)}</a>${trail}`;
  });
}

/**
 * What a link reads as: the address without the scheme, "www." and the query
 * string, cut at 40 characters. A search link with its filters spelled out
 * took three lines of the bubble.
 */
function linkLabel(escapedUrl: string): string {
  const bare = escapedUrl.replace(/^https?:\/\//, '').replace(/^www\./, '');
  const short = bare.split(/[?#]/)[0].replace(/\/$/, '');
  const label = short.length > 40 ? `${short.slice(0, 39)}…` : short;
  return bare.length > short.length + 1 ? `${label}…` : label;
}

const moreLink = (more: ListingMore | null | undefined, className: string): string => {
  const href = safeUrl(more?.url);
  return href && more?.label ? `<a class="${className}" href="${href}" target="_blank" rel="noopener noreferrer">${escapeHtml(more.label)} →</a>` : '';
};

const ctaText = (card: ListingCard, isTr: boolean) =>
  card.kind === 'service' ? (isTr ? 'Sayfasına git' : 'View page') : isTr ? 'İlana git' : 'View listing';

/**
 * HUMO Ask: the results as a list under the answer, one row each (photo,
 * name, price or place, a link to the listing), then "see all".
 */
export function renderListingList(cards: ListingCard[], more: ListingMore | null | undefined, locale: string | undefined): string {
  const isTr = locale !== 'en';
  const rows = cards
    .slice(0, 5)
    .map((card) => {
      const href = safeUrl(card.url);
      if (!href) return '';
      const image = safeUrl(card.imageUrl);
      const meta = [card.priceText, card.year, card.length ? `${card.length} m` : '', card.location].filter(Boolean).map(escapeHtml).join(' · ');
      return `
        <a class="ha-listing" href="${href}" target="_blank" rel="noopener noreferrer">
          ${image ? `<img class="ha-listing-img" src="${image}" alt="" loading="lazy" />` : '<span class="ha-listing-img"></span>'}
          <span class="ha-listing-body">
            <span class="ha-listing-title">${escapeHtml(card.title)}</span>
            ${meta ? `<span class="ha-listing-meta">${meta}</span>` : ''}
            <span class="ha-listing-cta">${ctaText(card, isTr)} →</span>
          </span>
        </a>`;
    })
    .join('');
  if (!rows) return '';
  return `<div class="ha-listings" role="list">${rows}${moreLink(more, 'ha-listing-more')}</div>`;
}

export function renderListingCards(cards: ListingCard[], locale: string | undefined, more?: ListingMore | null): string {
  const isTr = locale === 'tr';
  const items = cards
    .slice(0, 5)
    .map((card) => {
      const href = safeUrl(card.url);
      if (!href) return '';
      const image = safeUrl(card.imageUrl);
      const meta = [card.year, card.length ? `${card.length} m` : '', card.location].filter(Boolean).map(escapeHtml).join(' · ');
      return `
        <a class="humo-listing-card" href="${href}" target="_blank" rel="noopener noreferrer">
          ${image ? `<img class="humo-listing-card-image" src="${image}" alt="" loading="lazy" />` : '<div class="humo-listing-card-image"></div>'}
          <div class="humo-listing-card-body">
            <div class="humo-listing-card-title">${escapeHtml(card.title)}</div>
            ${card.priceText ? `<div class="humo-listing-card-price">${escapeHtml(card.priceText)}</div>` : ''}
            ${meta ? `<div class="humo-listing-card-meta">${meta}</div>` : ''}
            <div class="humo-listing-card-cta">${ctaText(card, isTr)} →</div>
          </div>
        </a>`;
    })
    .join('');
  return items ? `<div class="humo-listing-cards" role="list">${items}</div>${moreLink(more, 'humo-listing-more')}` : '';
}

/**
 * The page the visitor is writing from, sent with each message. On a
 * marketplace the backend recognises a listing page and answers about that
 * listing ("motoru kaç saat?"). Read at send time, so a single-page app's
 * route changes are followed. Only the page's own address and title.
 */
export function currentPage(): { url: string; title: string } | undefined {
  try {
    if (typeof window === 'undefined' || !/^https?:$/.test(window.location.protocol)) return undefined;
    return { url: window.location.href.slice(0, 600), title: (document.title || '').slice(0, 200) };
  } catch {
    return undefined;
  }
}
